import { LoadingButton } from "@mui/lab"
import { TextField } from "@mui/material"
import { t } from "i18next"
import React, { useRef, useState, useContext } from "react"
import { EnterLayout } from "../../../components/EnterLayout"
import { DATE_PATTERN_HH_mm_DD_MMM, convertToDateWithUtc, showErrorMessage } from "../../../helper/helper"
import { BidModel } from "../../../models/bid/BidModel"
import { Organisation } from "../../../models/organisation/Organisation"
import { colorDark, colorLight, colorPrimary, colorPrimaryDark } from "../../../rsc/colors/colors"
import { PopupEditPrice } from "./PopupEditPrice"
import { PriceBroadcastDialog } from "./PriceBroadcastDialog"
import { client } from "../../.."
import { UpsertBid } from "../../../api/api"
import { PriceReloadContext } from "./PriceContainer"
import { OrgBidModel } from "../../../models/bid/OrgBidModel"
import { WhatsAppIcon } from "../../contracts/TodayDeliveryIcons"

type RowPriceProps = {
    org: OrgBidModel
    price: BidModel
    isEditable: (org: OrgBidModel, price: BidModel) => boolean
    getErrorReason: (org: OrgBidModel) => string | null
}

export function PriceRow(props: RowPriceProps) {

    const [newPrice, setNewPrice] = useState(props.price?.price_per_quantity_unit?.toString() ?? '')
    const [onPriceError, setOnPriceError] = useState(false)
    const [loading, setLoading] = React.useState(false)
    const [displayPopup, setDisplayPopup] = useState(false)
    const [displayBroadcast, setDisplayBroadcast] = useState(false)

    const { reload } = useContext(PriceReloadContext)

    function isPriceSet(): boolean {
        return props.price?.price_per_quantity_unit !== undefined && props.price?.price_per_quantity_unit !== null
    }

    const updatePrice = (value: string) => {
        onCancel()
        let p = parseInt(value)
        console.log(p, value)
        if (p || value == null) {
            setLoading(true)
            setOnPriceError(false)
            client.mutate({
                mutation: UpsertBid,
                variables: {
                    input: {
                        organisation_id: props.org.organisationId,
                        bids: [
                            {
                                price_per_kg: p,
                                market_day: props.price.market_day
                            }
                        ]
                    }
                },
                context: {
                    headers: {
                        "role": "usermill"
                    }
                }
            }).then((data) => {
                setLoading(false)
                reload()
            }, (e: TypeError) => {
                setLoading(false)
                showErrorMessage(e)
            })
        } else {
            setLoading(false)
            setOnPriceError(true)
        }
    }

    function onCancel() {
        setDisplayPopup(false)
    }

    function onDelete() {
        onCancel()
        setLoading(true)
        updatePrice(null)
        setNewPrice('')
    }

    function getStyleForButton(): React.CSSProperties {
        if (!props.isEditable(props.org, props.price)) {
            return {
                backgroundColor: "#F5F5F5",
                color: colorDark()
            }
        }

        if (loading) {
            return {
                backgroundColor: "grey",
                color: colorLight()
            }
        } else {
            return {
                backgroundColor: isPriceSet() ? colorPrimaryDark() : colorPrimary(),
                color: colorLight()
            }
        }
    }

    function onInputChange(value: string) {
        const re = /^[0-9]+$/

        if (value === '' || re.test(value)) {
            setNewPrice(value)
        }
    }

    function getPriceLayout() {
        if (!isPriceSet()) {
            return (<div style={{ display: "flex", alignItems: "center" }}>
                <p className="font-14 greyColor" style={{ display: "inline-block", margin: "0 5px 0 0", fontWeight: "500" }}>Rp/Kg</p>
                <TextField
                    variant="standard"
                    disabled={!props.isEditable(props.org, props.price)}
                    value={newPrice}
                    error={onPriceError}
                    type='numeric'
                    inputProps={{ className:"font-24", maxLength: 4, style: { textAlign: 'end', fontWeight: "700", width: "60px", maxWidth: "5ch" } }}
                    onChange={(e) => onInputChange(e.target.value)}
                />
            </div>)
        } else {
            return <div style={{ display: "flex", alignItems: "center" }}>
                <p className="font-14" style={{ display: "inline-block", margin: "0" }}>Rp/Kg</p>
                <p className="font-24" style={{ display: "inline-block", margin: "0 0 0 5px", textAlign: 'end', fontWeight: "700" }}>{props.price.price_per_quantity_unit}</p>
            </div>
        }
    }

    return (
        <EnterLayout onClick={() => {
            if (isPriceSet()) {
                setDisplayPopup(true)
            } else {
                updatePrice(newPrice)
            }
        }}>
            <div>
                {isPriceSet() &&
                    <p className='primaryColor marginTop16 capitalize bold pempem-flex-row-center'>{t('price_setter_name', { name: props.price.updated_by, date: convertToDateWithUtc(props.price.updated_at, DATE_PATTERN_HH_mm_DD_MMM) })}</p>
                }
                <div className="" style={{ display: "flex", alignItems: "center", marginTop: "10px", paddingLeft: "10px" }}>
                    <p style={{ marginRight: "20px", flexShrink: "0", flex: 1 }}>{props.price.market_day}</p>
                    <div style={{ display: "flex", justifyContent: "end", flexDirection: "row", alignItems: "center" }}>
                        {getPriceLayout()}
                        <PopupEditPrice price={newPrice} onSave={(np) => updatePrice(np)} onDelete={onDelete} onCancel={onCancel} open={displayPopup} />
                        {
                            isPriceSet() ?
                                <LoadingButton
                                    disabled={!props.isEditable(props.org, props.price)}
                                    style={{ marginLeft: "10px", width: "100px", ...getStyleForButton() }}
                                    variant="contained"
                                    loading={loading}
                                    onClick={() => {
                                        setDisplayPopup(true)
                                    }}>
                                    {t('edit')}
                                </LoadingButton>
                                : <LoadingButton
                                    disabled={!props.isEditable(props.org, props.price)}
                                    style={{ marginLeft: "10px", width: "100px", ...getStyleForButton() }}
                                    variant="contained"
                                    loading={loading}
                                    onClick={() => updatePrice(newPrice)}>
                                    {t('save')}
                                </LoadingButton>
                        }
                        {/* WhatsApp broadcast button hidden for now.
                        {
                            isPriceSet() && <>
                                <button type="button" className="ui-btn ui-btn--outline ui-btn--sm" style={{ marginLeft: "10px", padding: "8px" }}
                                    aria-label={t('wa_broadcast_open')} title={t('wa_broadcast_open')}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setDisplayBroadcast(true)
                                    }}>
                                    <WhatsAppIcon size={18} />
                                </button>
                                <PriceBroadcastDialog org={props.org} price={props.price} open={displayBroadcast}
                                    onClose={() => setDisplayBroadcast(false)} />
                            </>
                        }
                        */}
                    </div>
                </div>
            </div>
        </EnterLayout>
    )
}
